import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import {
  FaTimes,
  FaSearch,
  FaMapMarkerAlt,
  FaStar,
  FaCrosshairs,
  FaLayerGroup,
  FaExpand,
  FaCompress,
  FaChevronRight,
  FaHotel,
  FaCompass,
  FaUserTie,
  FaSyncAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl, getBackendBaseUrl } from "../../utils/apiConfig";
import "./HotelMapExplorer.css";

// Curated high-resolution, unique Sri Lanka hotel photos
const CURATED_SRI_LANKA_HOTEL_PHOTOS = [
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1568084680786-a84f91d1153c?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1584132967334-10e028bd69f7?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?auto=format&fit=crop&w=900&q=80",
];

// Curated high-resolution, unique Sri Lanka tour photos
const CURATED_SRI_LANKA_TOUR_PHOTOS = [
  "https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=900&q=80", // Knuckles / Hiking
  "https://images.unsplash.com/photo-1576485290814-1c72aa4bbb8e?auto=format&fit=crop&w=900&q=80", // Tea hills
  "https://images.unsplash.com/photo-1568430462989-44163eb1752f?auto=format&fit=crop&w=900&q=80", // Whale watching
  "https://images.unsplash.com/photo-1588598198321-9735fd52455b?auto=format&fit=crop&w=900&q=80", // Cultural / Kandy dance
  "https://images.unsplash.com/photo-1586861635167-e5223aadc9fe?auto=format&fit=crop&w=900&q=80", // Ella Rock hike
  "https://images.unsplash.com/photo-1557050543-4d5f4e07ef46?auto=format&fit=crop&w=900&q=80", // Elephant safari
  "https://images.unsplash.com/photo-1589182373726-e4f658ab50f0?auto=format&fit=crop&w=900&q=80", // Horton plains
  "https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=900&q=80", // Snorkeling / Marine
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=900&q=80", // Ancient temple / Sigiriya
  "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=900&q=80", // Galle fort sunset
];

// Helper to pick unique hotel image
const getHotelPhotoFallback = (id, index = 0) => {
  const num = typeof id === "number" ? id : index;
  return CURATED_SRI_LANKA_HOTEL_PHOTOS[Math.abs(num) % CURATED_SRI_LANKA_HOTEL_PHOTOS.length];
};

// Helper to pick unique tour image
const getTourPhotoFallback = (id, index = 0) => {
  const num = typeof id === "number" ? id : index;
  return CURATED_SRI_LANKA_TOUR_PHOTOS[Math.abs(num) % CURATED_SRI_LANKA_TOUR_PHOTOS.length];
};

// Comprehensive fallback coordinates for Sri Lanka cities & destinations
const SRI_LANKA_CITY_COORDINATES = {
  colombo: { lat: 6.9271, lng: 79.8612 },
  kandy: { lat: 7.2906, lng: 80.6337 },
  galle: { lat: 6.0535, lng: 80.221 },
  ella: { lat: 6.8667, lng: 81.0466 },
  mirissa: { lat: 5.9482, lng: 80.4574 },
  sigiriya: { lat: 7.957, lng: 80.7603 },
  "nuwara eliya": { lat: 6.9497, lng: 80.7891 },
  trincomalee: { lat: 8.5874, lng: 81.2152 },
  jaffna: { lat: 9.6615, lng: 80.0255 },
  negombo: { lat: 7.2008, lng: 79.8736 },
  bentota: { lat: 6.4257, lng: 79.9965 },
  "arugam bay": { lat: 6.8415, lng: 81.834 },
  dambulla: { lat: 7.8742, lng: 80.6511 },
  yala: { lat: 6.3719, lng: 81.5173 },
  anuradhapura: { lat: 8.3114, lng: 80.4037 },
  polonnaruwa: { lat: 7.9403, lng: 81.0188 },
  tangalle: { lat: 6.0244, lng: 80.7941 },
  unawatuna: { lat: 6.0104, lng: 80.2483 },
  hikkaduwa: { lat: 6.1408, lng: 80.1009 },
  weligama: { lat: 5.9722, lng: 80.4286 },
  matara: { lat: 5.9549, lng: 80.555 },
  hambantota: { lat: 6.1248, lng: 81.1185 },
  kalutara: { lat: 6.5854, lng: 79.9607 },
  badulla: { lat: 6.9934, lng: 81.055 },
  ratnapura: { lat: 6.6828, lng: 80.4036 },
  kurunegala: { lat: 7.4863, lng: 80.3623 },
  batticaloa: { lat: 7.717, lng: 81.7 },
  mannar: { lat: 8.981, lng: 79.9044 },
  kilinochchi: { lat: 9.3803, lng: 80.377 },
  vavuniya: { lat: 8.7514, lng: 80.4971 },
  matale: { lat: 7.4674, lng: 80.6234 },
  knuckles: { lat: 7.4667, lng: 80.7833 },
  udawalawe: { lat: 6.474, lng: 80.898 },
  pinnawala: { lat: 7.301, lng: 80.387 },
  "horton plains": { lat: 6.8028, lng: 80.8092 },
};

// Helper: Parse coordinates string (lat, lng or google maps url)
const parseLocationCoordinates = (coordStr, address = "", name = "", index = 0) => {
  if (coordStr && typeof coordStr === "string") {
    const trimmed = coordStr.trim();
    // 1. Direct "lat, lng"
    const parts = trimmed.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng) && lat >= 5.0 && lat <= 10.5 && lng >= 78.5 && lng <= 82.5) {
        return { lat, lng };
      }
    }

    // 2. Google Maps @lat,lng
    const atMatch = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (atMatch) {
      const lat = parseFloat(atMatch[1]);
      const lng = parseFloat(atMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }

    // 3. Google Maps ?q=lat,lng
    const qMatch = trimmed.match(/[?&](?:q|query|ll)=(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (qMatch) {
      const lat = parseFloat(qMatch[1]);
      const lng = parseFloat(qMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
  }

  // 4. Fallback: match city keywords in address or entity name
  const searchText = `${address} ${name}`.toLowerCase();
  for (const [city, coords] of Object.entries(SRI_LANKA_CITY_COORDINATES)) {
    if (searchText.includes(city)) {
      const jitterLat = Math.sin(index * 1.5) * 0.015;
      const jitterLng = Math.cos(index * 1.5) * 0.015;
      return { lat: coords.lat + jitterLat, lng: coords.lng + jitterLng };
    }
  }

  // Spread across popular destinations if no city matched
  const popularSpots = Object.values(SRI_LANKA_CITY_COORDINATES);
  const spot = popularSpots[index % popularSpots.length];
  return {
    lat: spot.lat + Math.sin(index) * 0.012,
    lng: spot.lng + Math.cos(index) * 0.012,
  };
};

// Available Tile Layers: High-clarity, 100% Free & No API Key Required
const TILE_LAYERS = {
  osm: {
    name: "OpenStreetMap Standard",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
  esri_streets: {
    name: "Roads & Cities (Esri)",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom",
  },
  satellite: {
    name: "Satellite Hybrid",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye",
  },
  topo: {
    name: "Topographic (Esri)",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, METI",
  },
};

// Custom Leaflet Pin Icon for Hotels (Blue)
const createHotelPinIcon = (price, currency = "LKR", isSelected = false) => {
  const numPrice = parseFloat(price) || 15000;
  const formattedPrice = `${currency === "USD" ? "$" : "Rs "}${Math.round(numPrice).toLocaleString()}`;

  return L.divIcon({
    className: `sw-hotel-map-marker-container ${isSelected ? "selected" : ""}`,
    html: `
      <div class="sw-hotel-price-badge ${isSelected ? "active" : ""}">
        <span class="sw-badge-icon">🏨</span>
        <span class="sw-badge-text">${formattedPrice}</span>
        <div class="sw-badge-arrow hotel"></div>
      </div>
    `,
    iconSize: [92, 38],
    iconAnchor: [46, 38],
    popupAnchor: [0, -38],
  });
};

// Custom Leaflet Pin Icon for Tour Guides (Emerald/Teal)
const createTourPinIcon = (price, currency = "LKR", isSelected = false) => {
  const numPrice = parseFloat(price) || 6500;
  const formattedPrice = `${currency === "USD" ? "$" : "Rs "}${Math.round(numPrice).toLocaleString()}`;

  return L.divIcon({
    className: `sw-tour-map-marker-container ${isSelected ? "selected" : ""}`,
    html: `
      <div class="sw-tour-price-badge ${isSelected ? "active" : ""}">
        <span class="sw-badge-icon">🧭</span>
        <span class="sw-badge-text">${formattedPrice}</span>
        <div class="sw-badge-arrow tour"></div>
      </div>
    `,
    iconSize: [92, 38],
    iconAnchor: [46, 38],
    popupAnchor: [0, -38],
  });
};

const HotelMapModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all"); // 'all' | 'hotel' | 'tour'
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCity, setSelectedCity] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [tileMode, setTileMode] = useState("osm"); // Default to OpenStreetMap (watermark-free)
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [userLocating, setUserLocating] = useState(false);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markersGroupRef = useRef(null);
  const markersMapRef = useRef(new Map());

  // Popular Sri Lanka destinations filter chips
  const popularCities = [
    { id: "all", label: "All Sri Lanka" },
    { id: "colombo", label: "Colombo" },
    { id: "kandy", label: "Kandy" },
    { id: "galle", label: "Galle & South" },
    { id: "ella", label: "Ella" },
    { id: "sigiriya", label: "Sigiriya" },
    { id: "nuwara eliya", label: "Nuwara Eliya" },
    { id: "mirissa", label: "Mirissa" },
    { id: "bentota", label: "Bentota" },
    { id: "trincomalee", label: "Trincomalee" },
    { id: "jaffna", label: "Jaffna" },
  ];

  // Fetch both Hotels and Tour Guides from database
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = getApiBaseUrl();
      const backendBase = getBackendBaseUrl();

      // Parallel fetch for hotels & tours (42 hotel room listings + 41 tour listings = 83 total)
      const [listingsRes, hotelsRes, toursRes] = await Promise.all([
        axios.get(`${apiBase}/hotels/listings-with-details`).catch(() => null),
        axios.get(`${apiBase}/hotels/hotels-with-details`).catch(() => null),
        axios.get(`${apiBase}/guide-listing/guide/tour/tour-listings`).catch(() => null),
      ]);

      // 1. Process Hotels (Prefer all individual room listings, fallback to hotel properties)
      let rawHotels = [];
      if (listingsRes && listingsRes.data && Array.isArray(listingsRes.data) && listingsRes.data.length > 0) {
        rawHotels = listingsRes.data;
      } else if (hotelsRes && hotelsRes.data && Array.isArray(hotelsRes.data) && hotelsRes.data.length > 0) {
        rawHotels = hotelsRes.data;
      }

      const processedHotels = rawHotels.map((item, index) => {
        const name = item.name || item.hotelName || item.listingName || `SoloWay Stay ${index + 1}`;
        const address = item.address && item.address !== "123 Main Road, Sri Lanka" ? item.address : "Sri Lanka";
        const coords = parseLocationCoordinates(item.coordinates, address, name, index);

        let price = parseFloat(item.price) || 0;
        if (price <= 0) {
          price = 14500 + ((index * 2500) % 25000);
        }

        const rawImg = item.mainImage || item.logo || (item.hotel_images && item.hotel_images[0]?.url);
        const mainImage =
          rawImg && !rawImg.includes("logo.png") && !rawImg.includes("default_logo")
            ? rawImg.startsWith("http")
              ? rawImg
              : `${backendBase}${rawImg}`
            : getHotelPhotoFallback(item.id, index);

        return {
          type: "hotel",
          id: `hotel-${item.id || item.hotelId || index + 1}`,
          realId: item.id || item.hotelId || index + 1,
          name,
          title: name,
          address,
          coordinates: coords,
          price,
          currency: item.currency || "LKR",
          rating: item.averageRating && parseFloat(item.averageRating) > 0 ? item.averageRating : (4.6 + ((index % 5) * 0.1)).toFixed(1),
          reviews: item.reviewCount || 10 + (index * 3),
          mainImage,
          rawItem: item,
        };
      });

      // 2. Process Tour Guides
      let rawTours = [];
      if (toursRes && toursRes.data && toursRes.data.success && Array.isArray(toursRes.data.data)) {
        rawTours = toursRes.data.data;
      } else if (Array.isArray(toursRes?.data)) {
        rawTours = toursRes.data;
      }

      const processedTours = rawTours.map((item, index) => {
        const title = item.title || `Sri Lanka Tour with ${item.guide_name || "Local Guide"}`;
        const guideName = item.guide_name || "Professional Guide";
        const address = item.address || item.service_area || "Sri Lanka";
        const coords = parseLocationCoordinates(item.coordinates, address, title, index + 30);

        let price = parseFloat(item.price) || 0;
        if (price <= 0) {
          price = 6500 + ((index * 1200) % 18000);
        }

        const rawImg = item.mainImage || (item.images && item.images[0]?.url);
        const mainImage =
          rawImg && !rawImg.includes("placehold.co")
            ? rawImg.startsWith("http")
              ? rawImg
              : `${backendBase}${rawImg}`
            : getTourPhotoFallback(item.id, index);

        return {
          type: "tour",
          id: `tour-${item.id || index + 1}`,
          realId: item.id || index + 1,
          name: title,
          title,
          guideName,
          guideId: item.guide_id,
          address,
          serviceArea: item.service_area || "Sri Lanka",
          coordinates: coords,
          price,
          pricePerHour: item.price_per_hour,
          currency: "LKR",
          rating: item.average_rating && parseFloat(item.average_rating) > 0 ? item.average_rating : (4.7 + ((index % 4) * 0.1)).toFixed(1),
          reviews: item.review_count || 6 + (index * 2),
          mainImage,
          rawItem: item,
        };
      });

      setHotels(processedHotels);
      setTours(processedTours);
    } catch (err) {
      console.error("Failed to load map data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on modal open
  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  // Combined and filtered entities list based on category, search, and selected city
  const filteredItems = useMemo(() => {
    let combined = [];
    if (categoryFilter === "all") {
      combined = [...hotels, ...tours];
    } else if (categoryFilter === "hotel") {
      combined = hotels;
    } else if (categoryFilter === "tour") {
      combined = tours;
    }

    return combined.filter((item) => {
      const term = searchQuery.toLowerCase().trim();
      const matchesSearch =
        term === "" ||
        (item.name || "").toLowerCase().includes(term) ||
        (item.title || "").toLowerCase().includes(term) ||
        (item.guideName || "").toLowerCase().includes(term) ||
        (item.address || "").toLowerCase().includes(term) ||
        (item.serviceArea || "").toLowerCase().includes(term);

      const city = selectedCity.toLowerCase();
      const matchesCity =
        city === "all" ||
        (item.address || "").toLowerCase().includes(city) ||
        (item.name || "").toLowerCase().includes(city) ||
        (item.title || "").toLowerCase().includes(city) ||
        (item.serviceArea || "").toLowerCase().includes(city);

      return matchesSearch && matchesCity;
    });
  }, [hotels, tours, categoryFilter, searchQuery, selectedCity]);

  // Initialize Leaflet Map
  useEffect(() => {
    if (!isOpen || !mapContainerRef.current) return;

    if (!mapInstanceRef.current) {
      // Centered on Sri Lanka with soft bounds clamping
      const map = L.map(mapContainerRef.current, {
        center: [7.8731, 80.7718],
        zoom: 8,
        minZoom: 7,
        maxZoom: 18,
        maxBounds: [
          [5.0, 78.0],
          [10.8, 83.0],
        ],
        zoomControl: false,
      });

      // Add Zoom Control to top-right
      L.control.zoom({ position: "topright" }).addTo(map);

      // Add default tile layer (OpenStreetMap Standard - 100% watermark free)
      const tileLayer = L.tileLayer(TILE_LAYERS.osm.url, {
        maxZoom: 19,
        attribution: TILE_LAYERS.osm.attribution,
      }).addTo(map);

      tileLayerRef.current = tileLayer;

      // Group for markers
      const markersGroup = L.layerGroup().addTo(map);
      markersGroupRef.current = markersGroup;

      mapInstanceRef.current = map;
    }

    // Trigger invalidateSize to ensure correct tile rendering
    const timer = setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    }, 200);

    return () => {
      clearTimeout(timer);
    };
  }, [isOpen]);

  // Switch Tile Layer
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const config = TILE_LAYERS[tileMode] || TILE_LAYERS.osm;
    if (config && tileLayerRef.current) {
      mapInstanceRef.current.removeLayer(tileLayerRef.current);
      const newLayer = L.tileLayer(config.url, {
        maxZoom: 19,
        attribution: config.attribution,
      }).addTo(mapInstanceRef.current);
      tileLayerRef.current = newLayer;
    }
  }, [tileMode]);

  // Update Map Markers whenever filteredItems changes
  useEffect(() => {
    if (!mapInstanceRef.current || !markersGroupRef.current) return;

    // Clear existing markers
    markersGroupRef.current.clearLayers();
    markersMapRef.current.clear();

    const bounds = L.latLngBounds([]);

    filteredItems.forEach((item) => {
      if (!item.coordinates) return;
      const { lat, lng } = item.coordinates;
      bounds.extend([lat, lng]);

      const isSelected = selectedItem?.id === item.id;
      const isTour = item.type === "tour";

      const icon = isTour
        ? createTourPinIcon(item.price, item.currency, isSelected)
        : createHotelPinIcon(item.price, item.currency, isSelected);

      const marker = L.marker([lat, lng], { icon });

      // Build Rich HTML popup card
      const typeTag = isTour ? "🧭 Tour & Guide" : "🏨 Hotel / Stay";
      const metaExtra = isTour
        ? `<span class="sw-map-popup-guide"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg> ${item.guideName}</span>`
        : "";

      const buttonLabel = isTour ? "View Tour & Book Guide" : "View Hotel & Book Stay";
      const buttonClass = isTour ? "sw-map-popup-btn tour-primary" : "sw-map-popup-btn primary";

      const popupHtml = `
        <div class="sw-map-popup-card">
          <div class="sw-map-popup-image-box">
            <img src="${item.mainImage}" alt="${item.name}" class="sw-map-popup-img" />
            <span class="sw-map-popup-type-tag ${item.type}">${typeTag}</span>
            <span class="sw-map-popup-badge">${item.currency} ${Math.round(item.price).toLocaleString()}</span>
          </div>
          <div class="sw-map-popup-body">
            <h4 class="sw-map-popup-title">${item.name}</h4>
            ${metaExtra}
            <p class="sw-map-popup-address"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg> ${item.address}</p>
            <div class="sw-map-popup-meta">
              <span class="sw-map-popup-rating">⭐ ${item.rating} (${item.reviews} reviews)</span>
            </div>
            <div class="sw-map-popup-actions">
              <button class="${buttonClass}" id="popup-book-btn-${item.id}">
                ${buttonLabel}
              </button>
            </div>
          </div>
        </div>
      `;

      marker.bindPopup(popupHtml, {
        className: "sw-custom-leaflet-popup",
        maxWidth: 290,
        minWidth: 260,
      });

      marker.on("click", () => {
        setSelectedItem(item);
      });

      marker.on("popupopen", () => {
        const btn = document.getElementById(`popup-book-btn-${item.id}`);
        if (btn) {
          btn.onclick = () => {
            onClose();
            if (item.type === "tour") {
              navigate("/guideprofilebook", {
                state: {
                  guide: {
                    id: item.guideId || item.realId,
                    name: item.guideName,
                    ...item.rawItem,
                  },
                },
              });
            } else {
              navigate(`/travelerhotel/${item.realId}`, {
                state: { hotel: item.rawItem },
              });
            }
          };
        }
      });

      markersGroupRef.current.addLayer(marker);
      markersMapRef.current.set(item.id, marker);
    });

    // If city selected and items found, fit bounds to that region
    if (selectedCity !== "all" && filteredItems.length > 0 && bounds.isValid()) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    }
  }, [filteredItems, selectedCity, selectedItem, navigate, onClose]);

  // Handle clicking an item in the sidebar
  const handleSelectItemFromList = (item) => {
    setSelectedItem(item);
    if (!mapInstanceRef.current || !item.coordinates) return;

    const { lat, lng } = item.coordinates;
    mapInstanceRef.current.flyTo([lat, lng], 14, { duration: 1.2 });

    const marker = markersMapRef.current.get(item.id);
    if (marker) {
      setTimeout(() => {
        marker.openPopup();
      }, 700);
    }
  };

  // Browser Geolocation (Find places near me)
  const handleLocateUser = () => {
    if (!navigator.geolocation || !mapInstanceRef.current) return;
    setUserLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocating(false);
        const { latitude, longitude } = pos.coords;
        mapInstanceRef.current.flyTo([latitude, longitude], 13, { duration: 1.5 });

        // Add temporary user location circle
        const userIcon = L.divIcon({
          className: "sw-user-gps-pulse-pin",
          html: `<div class="sw-gps-pulse"></div><div class="sw-gps-dot"></div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });
        L.marker([latitude, longitude], { icon: userIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup("<strong>You are here</strong>")
          .openPopup();
      },
      () => {
        setUserLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  // Reset to full Sri Lanka view
  const handleResetSriLankaView = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([7.8731, 80.7718], 8, { duration: 1.2 });
      setSelectedCity("all");
      setSearchQuery("");
      setSelectedItem(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`sw-hotel-map-modal-backdrop ${isOpen ? "open" : ""}`}>
      <div className={`sw-hotel-map-modal-card ${isFullscreen ? "fullscreen" : ""}`}>
        {/* Modal Top Bar */}
        <div className="sw-hotel-map-topbar">
          <div className="sw-topbar-left">
            <div className="sw-topbar-badge">
              <span className="sw-badge-pulse" />
              <FaMapMarkerAlt className="sw-badge-icon-pin" />
              <span>Sri Lanka Map Explorer</span>
            </div>
            <span className="sw-topbar-counter">
              Showing <strong>{filteredItems.length}</strong> verified hotels & guides across Sri Lanka
            </span>
          </div>

          <div className="sw-topbar-right">
            {/* Tile Layer Selector */}
            <div className="sw-map-layer-selector">
              <FaLayerGroup className="sw-layer-icon" />
              <select
                value={tileMode}
                onChange={(e) => setTileMode(e.target.value)}
                className="sw-layer-select"
                title="Change Map Style"
              >
                <option value="osm">🗺️ OpenStreetMap (Standard)</option>
                <option value="esri_streets">🛣️ Roads & Cities (Esri)</option>
                <option value="satellite">🛰️ Satellite Hybrid</option>
                <option value="topo">⛰️ Topographic Map</option>
              </select>
            </div>

            {/* Locate Me */}
            <button
              type="button"
              className="sw-topbar-icon-btn"
              onClick={handleLocateUser}
              title="Find my location"
              disabled={userLocating}
            >
              <FaCrosshairs className={userLocating ? "spin-icon" : ""} />
            </button>

            {/* Reset View */}
            <button
              type="button"
              className="sw-topbar-icon-btn"
              onClick={handleResetSriLankaView}
              title="Reset to Full Sri Lanka Map"
            >
              <FaSyncAlt />
            </button>

            {/* Toggle Fullscreen */}
            <button
              type="button"
              className="sw-topbar-icon-btn"
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen Map"}
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>

            {/* Close Modal */}
            <button
              type="button"
              className="sw-topbar-close-btn"
              onClick={onClose}
              title="Close Map (Esc)"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Modal Main Workspace */}
        <div className="sw-hotel-map-workspace">
          {/* Side Drawer: Category Tabs, Search, City Filters, & Cards List */}
          <div className={`sw-hotel-map-sidebar ${sidebarOpen ? "open" : "collapsed"}`}>
            <div className="sw-sidebar-header">
              {/* Category Filter Tabs */}
              <div className="sw-category-tabs">
                <button
                  type="button"
                  className={`sw-category-tab ${categoryFilter === "all" ? "active" : ""}`}
                  onClick={() => setCategoryFilter("all")}
                >
                  ✨ All ({hotels.length + tours.length})
                </button>
                <button
                  type="button"
                  className={`sw-category-tab hotel-tab ${categoryFilter === "hotel" ? "active" : ""}`}
                  onClick={() => setCategoryFilter("hotel")}
                >
                  <FaHotel /> Hotels ({hotels.length})
                </button>
                <button
                  type="button"
                  className={`sw-category-tab tour-tab ${categoryFilter === "tour" ? "active" : ""}`}
                  onClick={() => setCategoryFilter("tour")}
                >
                  <FaCompass /> Guides ({tours.length})
                </button>
              </div>

              {/* Search Box */}
              <div className="sw-sidebar-search-box">
                <FaSearch className="sw-sidebar-search-icon" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search hotel, tour guide, destination..."
                  className="sw-sidebar-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="sw-sidebar-search-clear"
                    onClick={() => setSearchQuery("")}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              {/* City Filter Pills */}
              <div className="sw-city-pills-scroll">
                {popularCities.map((city) => (
                  <button
                    key={city.id}
                    type="button"
                    className={`sw-city-pill ${selectedCity === city.id ? "active" : ""}`}
                    onClick={() => setSelectedCity(city.id)}
                  >
                    {city.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Items List */}
            <div className="sw-sidebar-hotel-list">
              {loading ? (
                <div className="sw-sidebar-loading">
                  <div className="sw-spinner" />
                  <p>Loading verified hotels & tour guides from database...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="sw-sidebar-empty">
                  <FaMapMarkerAlt className="sw-empty-icon" />
                  <h4>No Results Found</h4>
                  <p>Try clearing your search query or switching categories.</p>
                  <button
                    type="button"
                    className="sw-btn-reset-filters"
                    onClick={handleResetSriLankaView}
                  >
                    Show All Sri Lanka
                  </button>
                </div>
              ) : (
                filteredItems.map((item) => {
                  const isTour = item.type === "tour";
                  return (
                    <div
                      key={item.id}
                      className={`sw-sidebar-hotel-card ${isTour ? "tour-card" : "hotel-card"} ${
                        selectedItem?.id === item.id ? "selected" : ""
                      }`}
                      onClick={() => handleSelectItemFromList(item)}
                    >
                      <div className="sw-card-thumb-box">
                        <img
                          src={item.mainImage}
                          alt={item.name}
                          className="sw-card-thumb"
                          onError={(e) => {
                            e.target.src = isTour
                              ? getTourPhotoFallback(item.realId)
                              : getHotelPhotoFallback(item.realId);
                          }}
                        />
                        <span className={`sw-card-type-tag ${item.type}`}>
                          {isTour ? "🧭 Guide" : "🏨 Hotel"}
                        </span>
                        <span className="sw-card-price-pill">
                          {item.currency} {Math.round(item.price).toLocaleString()}
                        </span>
                      </div>

                      <div className="sw-card-info">
                        <h4 className="sw-card-name" title={item.name}>
                          {item.name}
                        </h4>
                        {isTour && (
                          <p className="sw-card-guide-name" title={`Guide: ${item.guideName}`}>
                            <FaUserTie /> Guide: {item.guideName}
                          </p>
                        )}
                        <p className="sw-card-address" title={item.address}>
                          <FaMapMarkerAlt /> {item.address}
                        </p>
                        <div className="sw-card-footer">
                          <span className="sw-card-rating">
                            <FaStar className="star-icon" /> {item.rating} ({item.reviews})
                          </span>
                          <span className="sw-card-view-link">
                            View Pin <FaChevronRight />
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Sidebar Toggle Tab Button */}
          <button
            type="button"
            className={`sw-sidebar-collapse-toggle ${sidebarOpen ? "open" : ""}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? "Hide Listing Drawer" : "Show Listing Drawer"}
          >
            <FaChevronRight className={`sw-toggle-chevron ${sidebarOpen ? "rotate" : ""}`} />
            <span>{sidebarOpen ? "Hide List" : "Show List"}</span>
          </button>

          {/* Leaflet Map Canvas */}
          <div className="sw-hotel-map-canvas-wrapper">
            <div ref={mapContainerRef} className="sw-hotel-leaflet-map-canvas"></div>

            {/* Floating Map City Label Overlay */}
            <div className="sw-map-bottom-info-pill">
              <span>📍 Showing verified road networks, hotels & local tour guides in Sri Lanka</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelMapModal;
